import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface ToggleProps {
	enabled: boolean;
	onChange: (enabled: boolean) => void;
	size?: 'sm' | 'md' | 'lg';
	disabled?: boolean;
	activeColor?: string;
	inactiveColor?: string;
}

const Toggle: React.FC<ToggleProps> = ({
	enabled,
	onChange,
	size = 'md',
	disabled = false,
	activeColor = 'bg-primary',
	inactiveColor = 'bg-gray-200 dark:bg-gray-700'
}) => {
	const { i18n } = useTranslation();
	const isRTL = i18n.language === 'ar';

	// Size classes
	const sizeClasses = {
		sm: {
			toggle: 'w-8 h-4',
			circle: 'w-3 h-3',
			translateX: 16
		},
		md: {
			toggle: 'w-11 h-6',
			circle: 'w-5 h-5',
			translateX: 20
		},
		lg: {
			toggle: 'w-14 h-7',
			circle: 'w-6 h-6',
			translateX: 28
		}
	};

	const { toggle, circle, translateX } = sizeClasses[size];

	return (
		<button
			type="button"
			className={`
        relative inline-flex items-center flex-shrink-0 rounded-full
        transition-colors ease-in-out duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-opacity-75
        ${toggle}
        ${enabled ? activeColor : inactiveColor}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
			role="switch"
			aria-checked={enabled}
			disabled={disabled}
			onClick={() => onChange(!enabled)}
		>
			<span className="sr-only">Toggle</span>
			<motion.span
				className={` ms-0.5
          pointer-events-none inline-block rounded-full bg-white shadow-lg 
          transform ring-0 transition ease-in-out duration-200
          ${circle}
        `}
				animate={{
					translateX: enabled ? (
						isRTL ? -translateX : translateX
					) : 0
				}}
			/>
		</button>
	);
};

export default Toggle;